/**
 * 状態異常システム: メインエンジン
 *
 * 状態異常の付与、解除、効果計算を管理するエンジン。
 *
 * @see src/ルール/status-effect-system-design.md
 */

import {
  STATUS_EFFECT_TYPES,
  STATUS_EFFECT_METADATA,
  getStatusDisplayName,
} from './statusTypes';
import type { StatusEffectTypeValue } from './statusTypes';
import type { CardData } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import { hasHikou } from '../keywordAbilities';

// ========================================
// 型定義
// ========================================

/**
 * 状態異常オブジェクト
 * types/effect.ts の StatusEffect と互換性を維持するため、
 * 一部のプロパティはオプショナルに変更
 */
export interface StatusEffect {
  id?: string;
  type: StatusEffectTypeValue;
  source: string | null;
  sourceName: string | null;
  appliedTurn?: number;
  duration: number;
  removeChance?: number;
  removeOnDamage?: boolean;
  usageCount?: number;
  maxUsage?: number;
  value?: number;
  stackable?: boolean;
  expiresAfterEndPhases?: number | null;
  // 寄生効果用
  parasiteCard?: CardData | null;
  parasiteOwner?: PlayerNumber | null;
  effectNegated?: boolean;
}

/** プレイヤー状態異常オブジェクト */
export interface PlayerStatusEffect {
  id: string;
  type: StatusEffectTypeValue;
  source: string | null;
  sourceName: string | null;
  appliedTurn: number;
  value: number;
  duration: number;
  remainingTurns: number;
}

/** 状態異常付与オプション */
export interface ApplyStatusOptions {
  source?: string;
  sourceName?: string;
  currentTurn?: number;
  duration?: number;
  removeChance?: number;
  removeOnDamage?: boolean;
  maxUsage?: number;
  value?: number;
  stackable?: boolean;
  expiresAfterEndPhases?: number;
  // 寄生効果用
  parasiteCard?: CardData;
  parasiteOwner?: PlayerNumber;
  effectNegated?: boolean;
}

/**
 * 状態異常を持つモンスター
 * MonsterInstance との互換性を維持するため、statusEffects はオプショナル
 */
export interface MonsterWithStatus {
  statusEffects?: StatusEffect[];
  currentAttack?: number;
  currentHp?: number;
  name?: string;
  attack?: number;
  hp?: number;
  uniqueId?: string;
  // MonsterInstance との互換性のため
  canAttack?: boolean;
  id?: string;
  attribute?: string;
  cost?: number;
  type?: string;
  effect?: string;
}

/** applyStatus の結果 */
export interface ApplyStatusResult {
  success: boolean;
  reason?: string;
  statusEffect?: StatusEffect;
}

/** processTurnStart の結果 */
export interface ProcessTurnStartResult {
  monster: MonsterWithStatus;
  removedEffects: StatusEffect[];
  parasiteAtkReduction: number;
}

/** processEndPhase の結果 */
export interface ProcessEndPhaseResult {
  monster: MonsterWithStatus;
  removedEffects: StatusEffect[];
  atkReduction: number;
}

/** processOpponentEndPhase の結果 */
export interface ProcessOpponentEndPhaseResult {
  monster: MonsterWithStatus;
  parasiteEffectNegationRemoved: boolean;
}

/** 攻撃力修正詳細 */
export interface AttackModifierDetails {
  multiplier: number;
  flatModifier: number;
}

/** ダメージ軽減結果 */
export interface DamageReductionResult {
  reduction: number;
  usedGuard: boolean;
  updatedMonster: MonsterWithStatus;
}

/** applyPlayerStatus の結果 */
export interface ApplyPlayerStatusResult {
  success: boolean;
  reason?: string;
  statusEffect?: PlayerStatusEffect;
}

/** processPlayerEndPhase の結果 */
export interface ProcessPlayerEndPhaseResult {
  damage: number;
  effects: PlayerStatusEffect[];
}

/** processPlayerTurnStart の結果 */
export interface ProcessPlayerTurnStartResult {
  updatedEffects: PlayerStatusEffect[];
  expiredEffects: PlayerStatusEffect[];
}

/** canAttackWithReason の結果 */
export interface CanAttackWithReasonResult {
  canAttack: boolean;
  reason?: string;
}

/** canUseSkillWithReason の結果 */
export interface CanUseSkillWithReasonResult {
  canUseSkill: boolean;
  reason?: string;
}

/** canUseTriggerWithReason の結果 */
export interface CanUseTriggerWithReasonResult {
  canUseTrigger: boolean;
  reason?: string;
}

/** 寄生情報 */
export interface ParasiteInfo {
  parasiteCard: CardData;
  parasiteOwner: PlayerNumber;
}

/**
 * 状態異常エンジンクラス
 */
class StatusEffectEngine {
  /**
   * システムをクリア（ゲーム初期化時）
   */
  clear() {
    // ステートレスなエンジンなので特に何もしない
    // モンスターの状態異常はモンスターオブジェクト内で管理
  }

  // ========================================
  // モンスター状態異常: ライフサイクル管理
  // ========================================

  /**
   * モンスターに状態異常を付与
   * @param monster - 対象モンスター
   * @param statusType - 状態異常タイプ
   * @param options - オプション
   * @returns { success: boolean, reason?: string }
   */
  applyStatus(
    monster: MonsterWithStatus | null,
    statusType: StatusEffectTypeValue,
    options: ApplyStatusOptions = {}
  ): ApplyStatusResult {
    if (!monster) {
      return { success: false, reason: '対象が存在しません' };
    }

    // statusEffects配列がなければ初期化
    if (!monster.statusEffects) {
      monster.statusEffects = [];
    }

    const meta = STATUS_EFFECT_METADATA[statusType];
    if (!meta) {
      return { success: false, reason: '不明な状態異常タイプです' };
    }

    // 【彷徨い】耐性チェック: 飛行を持つモンスターは彷徨いにならない
    if (statusType === STATUS_EFFECT_TYPES.WANDERING && hasHikou(monster)) {
      return { success: false, reason: '【飛行】を持つモンスターは【彷徨い】にならない' };
    }

    // 重複チェック（同じ状態異常は上書き）
    const existingIndex = monster.statusEffects.findIndex(s => s.type === statusType);
    if (existingIndex !== -1) {
      // 上書き
      monster.statusEffects.splice(existingIndex, 1);
    }

    // 状態異常オブジェクトを作成
    const statusEffect = {
      id: `${statusType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: statusType,
      source: options.source || null,
      sourceName: options.sourceName || null,
      appliedTurn: options.currentTurn || 0,
      duration: options.duration !== undefined ? options.duration : (meta.defaultDuration || 1),
      removeChance: options.removeChance !== undefined ? options.removeChance : (meta.defaultRemoveChance || 0),
      removeOnDamage: options.removeOnDamage || false,
      usageCount: 0,
      maxUsage: options.maxUsage || meta.maxUsage || -1, // -1 = 無制限
      value: options.value || 0,
      stackable: options.stackable || false,
      // エンドフェイズ回数ベースの解除（「次のターン終了時まで」= 2）
      expiresAfterEndPhases: options.expiresAfterEndPhases !== undefined ? options.expiresAfterEndPhases : null,
      // 寄生効果用の追加プロパティ
      parasiteCard: options.parasiteCard || null,
      parasiteOwner: options.parasiteOwner || null,
      effectNegated: options.effectNegated !== undefined ? options.effectNegated : false,
    };

    monster.statusEffects.push(statusEffect);

    return { success: true, statusEffect };
  }

  /**
   * モンスターから状態異常を解除
   * @param monster - 対象モンスター
   * @param statusType - 状態異常タイプ（省略で全解除）
   * @returns 解除された状態異常
   */
  removeStatus(
    monster: MonsterWithStatus | null,
    statusType: StatusEffectTypeValue | null = null
  ): StatusEffect[] {
    if (!monster || !monster.statusEffects) {
      return [];
    }

    let removed: StatusEffect[] = [];

    if (statusType) {
      // 特定の状態異常を解除
      const index = monster.statusEffects.findIndex(s => s.type === statusType);
      if (index !== -1) {
        removed = monster.statusEffects.splice(index, 1);
      }
    } else {
      // 全状態異常を解除
      removed = [...monster.statusEffects];
      monster.statusEffects = [];
    }

    return removed;
  }

  /**
   * ターン開始時の処理
   * - 確率解除判定（眠り、凍結など）
   * - 寄生のATK減少
   * @param monster - 対象モンスター
   * @returns { monster: Object, removedEffects: Array, parasiteAtkReduction: number }
   */
  processTurnStart(monster: MonsterWithStatus | null): ProcessTurnStartResult {
    if (!monster || !monster.statusEffects || monster.statusEffects.length === 0) {
      return { monster: monster || {}, removedEffects: [], parasiteAtkReduction: 0 };
    }

    const removedEffects: StatusEffect[] = [];
    const remainingEffects: StatusEffect[] = [];
    let parasiteAtkReduction = 0;
    let newAttack = monster.currentAttack ?? 0;

    monster.statusEffects.forEach(status => {
      // 寄生のATK減少処理（毎ターン開始時）
      if (status.type === STATUS_EFFECT_TYPES.PARASITE) {
        const atkDown = status.value || 500;
        newAttack = Math.max(0, newAttack - atkDown);
        parasiteAtkReduction = atkDown;
        remainingEffects.push(status);
        return;
      }

      // 確率解除判定
      const removeChance = status.removeChance ?? 0;
      if (removeChance > 0 && Math.random() < removeChance) {
        removedEffects.push(status);
      } else {
        remainingEffects.push(status);
      }
    });

    // イミュータブルに更新
    const updatedMonster: MonsterWithStatus = {
      ...monster,
      currentAttack: newAttack,
      statusEffects: remainingEffects,
    };

    return { monster: updatedMonster, removedEffects, parasiteAtkReduction };
  }

  /**
   * エンドフェイズ時の処理
   * - 持続ターン減少
   * - エンドフェイズ回数カウントダウン（expiresAfterEndPhases）
   * - 期限切れ解除
   * - 深蝕の攻撃力減少
   * @param monster - 対象モンスター
   * @returns { monster: Object, removedEffects: Array, atkReduction: number }
   */
  processEndPhase(
    monster: MonsterWithStatus | null,
    options?: { skipDurationDecrement?: StatusEffectTypeValue[] }
  ): ProcessEndPhaseResult {
    if (!monster || !monster.statusEffects || monster.statusEffects.length === 0) {
      return { monster: monster || {}, removedEffects: [], atkReduction: 0 };
    }

    const removedEffects: StatusEffect[] = [];
    const remainingEffects: StatusEffect[] = [];
    let atkReduction = 0;
    let newAttack = monster.currentAttack ?? 0;
    const skipTypes = options?.skipDurationDecrement || [];

    monster.statusEffects.forEach(status => {
      // 深蝕の処理
      if (status.type === STATUS_EFFECT_TYPES.CORRODE) {
        const atkDown = status.value || 100;
        newAttack = Math.max(0, newAttack - atkDown);
        atkReduction += atkDown;
      }

      // スキップ対象の状態異常はdurationを減らさない
      if (skipTypes.includes(status.type as StatusEffectTypeValue)) {
        remainingEffects.push(status);
        return;
      }

      // エンドフェイズ回数ベースの解除判定
      if (status.expiresAfterEndPhases !== null && status.expiresAfterEndPhases !== undefined) {
        const newEndPhases = status.expiresAfterEndPhases - 1;
        if (newEndPhases <= 0) {
          // 期限切れ
          removedEffects.push(status);
        } else {
          remainingEffects.push({ ...status, expiresAfterEndPhases: newEndPhases });
        }
        return; // expiresAfterEndPhasesが設定されている場合はdurationは無視
      }

      // 持続ターン減少（duration > 0 の場合のみ）
      if (status.duration > 0) {
        const newDuration = status.duration - 1;
        if (newDuration <= 0) {
          // 期限切れ
          removedEffects.push(status);
        } else {
          remainingEffects.push({ ...status, duration: newDuration });
        }
      }
      // duration === 0 はターン終了時まで（このターンで解除）
      else if (status.duration === 0) {
        removedEffects.push(status);
      }
      // duration === -1 は永続（解除しない）
      else {
        remainingEffects.push(status);
      }
    });

    // イミュータブルに更新
    const updatedMonster: MonsterWithStatus = {
      ...monster,
      currentAttack: newAttack,
      statusEffects: remainingEffects,
    };

    return { monster: updatedMonster, removedEffects, atkReduction };
  }

  /**
   * 相手エンドフェイズ時の処理（寄生の効果無効解除）
   * @param monster - 対象モンスター
   * @param currentPlayer - 現在のターンプレイヤー
   * @returns { monster: Object, parasiteEffectNegationRemoved: boolean }
   */
  processOpponentEndPhase(
    monster: MonsterWithStatus | null,
    currentPlayer: PlayerNumber
  ): ProcessOpponentEndPhaseResult {
    if (!monster || !monster.statusEffects || monster.statusEffects.length === 0) {
      return { monster: monster || {}, parasiteEffectNegationRemoved: false };
    }

    let parasiteEffectNegationRemoved = false;
    const updatedEffects = monster.statusEffects.map(status => {
      // 寄生の効果無効解除判定
      // 寄生者（parasiteOwner）と現在ターンプレイヤーが異なる場合、
      // つまり被寄生者のエンドフェイズで効果無効を解除
      if (status.type === STATUS_EFFECT_TYPES.PARASITE && status.effectNegated) {
        if (status.parasiteOwner !== currentPlayer) {
          parasiteEffectNegationRemoved = true;
          return { ...status, effectNegated: false };
        }
      }
      return status;
    });

    const updatedMonster = {
      ...monster,
      statusEffects: updatedEffects,
    };

    return { monster: updatedMonster, parasiteEffectNegationRemoved };
  }

  // ========================================
  // 状態チェック
  // ========================================

  /**
   * 特定の状態異常を持っているか
   * @param monster - 対象モンスター
   * @param statusType - 状態異常タイプ
   * @returns boolean
   */
  hasStatus(monster: MonsterWithStatus | null, statusType: StatusEffectTypeValue): boolean {
    return monster?.statusEffects?.some(s => s.type === statusType) || false;
  }

  /**
   * 状態異常一覧を取得
   * @param monster - 対象モンスター
   * @returns 状態異常配列
   */
  getActiveStatuses(monster: MonsterWithStatus | null): StatusEffect[] {
    return monster?.statusEffects || [];
  }

  /**
   * 攻撃可能かチェック
   * @param monster - 対象モンスター
   * @returns boolean
   */
  canAttack(monster: MonsterWithStatus | null): boolean {
    if (!monster) return false;

    // 眠り、凍結、行動不能、檻
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.FREEZE)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.STUN)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return false;

    return true;
  }

  /**
   * 技使用可能かチェック
   * @param monster - 対象モンスター
   * @returns boolean
   */
  canUseSkill(monster: MonsterWithStatus | null): boolean {
    if (!monster) return false;

    // 眠り、行動不能、効果無効、雷撃、檻、彷徨い
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.STUN)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SILENCE)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.THUNDER)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.WANDERING)) return false;

    // 寄生（effectNegatedがtrueの場合のみ）
    if (this.isParasiteEffectNegated(monster)) return false;

    return true;
  }

  /**
   * トリガー発動可能かチェック
   * @param monster - 対象モンスター
   * @returns boolean
   */
  canUseTrigger(monster: MonsterWithStatus | null): boolean {
    if (!monster) return false;

    // 眠り、効果無効、檻
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SILENCE)) return false;
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return false;

    // 寄生（effectNegatedがtrueの場合のみ）
    if (this.isParasiteEffectNegated(monster)) return false;

    return true;
  }

  /**
   * 寄生による効果無効が有効かチェック
   * @param monster - 対象モンスター
   * @returns boolean
   */
  isParasiteEffectNegated(monster: MonsterWithStatus | null): boolean {
    if (!monster?.statusEffects) return false;
    const parasite = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.PARASITE);
    return parasite?.effectNegated === true;
  }

  /**
   * 寄生カード情報を取得（破壊時墓地送り用）
   * @param monster - 対象モンスター
   * @returns { parasiteCard, parasiteOwner } または null
   */
  getParasiteInfo(monster: MonsterWithStatus | null): ParasiteInfo | null {
    if (!monster?.statusEffects) return null;
    const parasite = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.PARASITE);
    if (parasite && parasite.parasiteCard && parasite.parasiteOwner !== undefined && parasite.parasiteOwner !== null) {
      return {
        parasiteCard: parasite.parasiteCard,
        parasiteOwner: parasite.parasiteOwner,
      };
    }
    return null;
  }

  /**
   * 行動をブロックしている状態異常名を取得
   * @param monster - 対象モンスター
   * @param actionType - 'attack' または 'skill'
   * @returns 状態異常の表示名
   */
  getBlockingStatusName(monster: MonsterWithStatus | null, actionType: 'attack' | 'skill'): string {
    if (!monster) return '不明な状態';

    if (actionType === 'attack') {
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return getStatusDisplayName(STATUS_EFFECT_TYPES.SLEEP);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.FREEZE)) return getStatusDisplayName(STATUS_EFFECT_TYPES.FREEZE);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.STUN)) return getStatusDisplayName(STATUS_EFFECT_TYPES.STUN);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return getStatusDisplayName(STATUS_EFFECT_TYPES.CAGED);
    } else if (actionType === 'skill') {
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return getStatusDisplayName(STATUS_EFFECT_TYPES.SLEEP);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.STUN)) return getStatusDisplayName(STATUS_EFFECT_TYPES.STUN);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SILENCE)) return getStatusDisplayName(STATUS_EFFECT_TYPES.SILENCE);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.THUNDER)) return getStatusDisplayName(STATUS_EFFECT_TYPES.THUNDER);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return getStatusDisplayName(STATUS_EFFECT_TYPES.CAGED);
      if (this.hasStatus(monster, STATUS_EFFECT_TYPES.WANDERING)) return getStatusDisplayName(STATUS_EFFECT_TYPES.WANDERING);
      if (this.isParasiteEffectNegated(monster)) return getStatusDisplayName(STATUS_EFFECT_TYPES.PARASITE);
    }

    return '不明な状態';
  }

  // 後方互換性のための古い形式のメソッド（オブジェクト形式を返す）
  canAttackWithReason(monster: MonsterWithStatus | null): CanAttackWithReasonResult {
    if (!monster) return { canAttack: false, reason: '対象が存在しません' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return { canAttack: false, reason: '眠り状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.FREEZE)) return { canAttack: false, reason: '凍結状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.STUN)) return { canAttack: false, reason: '行動不能状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return { canAttack: false, reason: '檻状態' };
    return { canAttack: true };
  }

  canUseSkillWithReason(monster: MonsterWithStatus | null): CanUseSkillWithReasonResult {
    if (!monster) return { canUseSkill: false, reason: '対象が存在しません' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return { canUseSkill: false, reason: '眠り状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.STUN)) return { canUseSkill: false, reason: '行動不能状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SILENCE)) return { canUseSkill: false, reason: '効果無効状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.THUNDER)) return { canUseSkill: false, reason: '雷撃状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return { canUseSkill: false, reason: '檻状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.WANDERING)) return { canUseSkill: false, reason: '彷徨い状態' };
    if (this.isParasiteEffectNegated(monster)) return { canUseSkill: false, reason: '寄生状態' };
    return { canUseSkill: true };
  }

  canUseTriggerWithReason(monster: MonsterWithStatus | null): CanUseTriggerWithReasonResult {
    if (!monster) return { canUseTrigger: false, reason: '対象が存在しません' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SLEEP)) return { canUseTrigger: false, reason: '眠り状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.SILENCE)) return { canUseTrigger: false, reason: '効果無効状態' };
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) return { canUseTrigger: false, reason: '檻状態' };
    if (this.isParasiteEffectNegated(monster)) return { canUseTrigger: false, reason: '寄生状態' };
    return { canUseTrigger: true };
  }

  // ========================================
  // ステータス修正
  // ========================================

  /**
   * 攻撃力が強制的に0になるか判定（彷徨い状態）
   * @param monster - 対象モンスター
   * @returns 攻撃力が0に強制されるかどうか
   */
  isAttackZeroed(monster: MonsterWithStatus | null): boolean {
    if (!monster) return false;
    return this.hasStatus(monster, STATUS_EFFECT_TYPES.WANDERING);
  }

  /**
   * 攻撃力修正を計算（単純な数値を返す）
   * 注意: 彷徨い状態の場合は isAttackZeroed() で別途判定が必要
   * @param monster - 対象モンスター
   * @returns 攻撃力修正値（加算値、倍率は currentAttack に適用済みと想定）
   */
  getAttackModifier(monster: MonsterWithStatus | null): number {
    if (!monster?.statusEffects) {
      return 0;
    }

    let modifier = 0;
    let baseAtk = monster.currentAttack || 0;

    // 凍結: 50%（攻撃力を半減させる分を減算として計算）
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.FREEZE)) {
      modifier -= Math.floor(baseAtk * 0.5);
    }

    // 雷撃: -500（固定値）
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.THUNDER)) {
      modifier -= 500;
    }

    // 覚醒: 効果値分上昇
    const awakened = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.AWAKENED);
    if (awakened) {
      modifier += awakened.value ?? 0;
    }

    // ATK_UP: バフ値を加算
    const atkUp = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.ATK_UP);
    if (atkUp) {
      modifier += atkUp.value ?? 0;
    }

    // ATK_DOWN: デバフ値を減算
    const atkDown = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.ATK_DOWN);
    if (atkDown) {
      modifier -= atkDown.value ?? 0;
    }

    return modifier;
  }

  /**
   * 攻撃力修正を計算（詳細オブジェクトを返す）
   * @param monster - 対象モンスター
   * @returns { multiplier: number, flatModifier: number }
   */
  getAttackModifierDetails(monster: MonsterWithStatus | null): AttackModifierDetails {
    let multiplier = 1.0;
    let flatModifier = 0;

    if (!monster?.statusEffects) {
      return { multiplier, flatModifier };
    }

    // 凍結: 50%
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.FREEZE)) {
      multiplier *= 0.5;
    }

    // 雷撃: -500（固定値）
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.THUNDER)) {
      flatModifier -= 500;
    }

    // 覚醒: 効果値分上昇
    const awakened = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.AWAKENED);
    if (awakened) {
      flatModifier += awakened.value ?? 0;
    }

    // ATK_UP: バフ値を加算
    const atkUp = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.ATK_UP);
    if (atkUp) {
      flatModifier += atkUp.value ?? 0;
    }

    // ATK_DOWN: デバフ値を減算
    const atkDown = monster.statusEffects.find(s => s.type === STATUS_EFFECT_TYPES.ATK_DOWN);
    if (atkDown) {
      flatModifier -= atkDown.value ?? 0;
    }

    return { multiplier, flatModifier };
  }

  /**
   * 被ダメージ倍率を計算
   * @param monster - 対象モンスター
   * @returns ダメージ倍率（1.0 = 変化なし）
   */
  getDamageMultiplier(monster: MonsterWithStatus | null): number {
    let multiplier = 1.0;

    if (!monster?.statusEffects) {
      return multiplier;
    }

    // 濡れ: ダメージ2倍
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.WET)) {
      multiplier *= 2.0;
    }

    return multiplier;
  }

  /**
   * ダメージ軽減を計算（守護など）
   * @param monster - 対象モンスター
   * @param damage - 元ダメージ
   * @returns { reduction: number, usedGuard: boolean, updatedMonster: Object }
   */
  calculateDamageReduction(monster: MonsterWithStatus | null, damage: number): DamageReductionResult {
    if (!monster?.statusEffects || monster.statusEffects.length === 0) {
      return { reduction: 0, usedGuard: false, updatedMonster: monster || {} };
    }

    // 無敵チェック
    if (this.hasStatus(monster, STATUS_EFFECT_TYPES.INVINCIBLE)) {
      return { reduction: damage, usedGuard: false, updatedMonster: monster };
    }

    // ダメージシールドチェック（固定値軽減、守護より先に適用）
    let totalFlatReduction = 0;
    for (const status of monster.statusEffects) {
      if (status.type === STATUS_EFFECT_TYPES.DAMAGE_SHIELD && (status.value ?? 0) > 0) {
        totalFlatReduction += status.value!;
      }
    }

    // 守護チェック
    const guardIndex = monster.statusEffects.findIndex(s => s.type === STATUS_EFFECT_TYPES.GUARD);
    if (guardIndex !== -1) {
      const guard = monster.statusEffects[guardIndex];
      const reductionRate = STATUS_EFFECT_METADATA[STATUS_EFFECT_TYPES.GUARD].effects.damageReduction ?? 0.5;
      // 守護の割合軽減はシールド軽減後のダメージに適用
      const damageAfterShield = Math.max(0, damage - totalFlatReduction);
      const guardReduction = Math.floor(damageAfterShield * reductionRate);
      const totalReduction = Math.min(damage, totalFlatReduction + guardReduction);

      // 使用回数上限に達したら解除（イミュータブルに更新）
      const newUsageCount = (guard.usageCount ?? 0) + 1;
      const maxUsage = guard.maxUsage ?? 0;
      const shouldRemove = maxUsage > 0 && newUsageCount >= maxUsage;

      let newStatusEffects;
      if (shouldRemove) {
        // 守護を解除
        newStatusEffects = monster.statusEffects.filter((_, idx) => idx !== guardIndex);
      } else {
        // 使用回数を更新
        newStatusEffects = monster.statusEffects.map((s, idx) =>
          idx === guardIndex ? { ...s, usageCount: newUsageCount } : s
        );
      }

      const updatedMonster = {
        ...monster,
        statusEffects: newStatusEffects,
      };

      return { reduction: totalReduction, usedGuard: true, updatedMonster };
    }

    // ダメージシールドのみ
    if (totalFlatReduction > 0) {
      const reduction = Math.min(damage, totalFlatReduction);
      return { reduction, usedGuard: false, updatedMonster: monster };
    }

    return { reduction: 0, usedGuard: false, updatedMonster: monster };
  }

  // ========================================
  // プレイヤー状態異常
  // ========================================

  /**
   * プレイヤーに状態異常を付与
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @param statusType - 状態異常タイプ
   * @param options - オプション
   * @returns { success: boolean, statusEffect?: Object }
   */
  applyPlayerStatus(
    playerStatusEffects: PlayerStatusEffect[],
    statusType: StatusEffectTypeValue,
    options: ApplyStatusOptions = {}
  ): ApplyPlayerStatusResult {
    const meta = STATUS_EFFECT_METADATA[statusType];
    if (!meta || meta.target !== 'player') {
      return { success: false, reason: 'プレイヤー状態異常ではありません' };
    }

    // 同じタイプの状態異常が既にある場合は上書き（毒以外）
    if (statusType !== STATUS_EFFECT_TYPES.POISON) {
      const existingIndex = playerStatusEffects.findIndex(s => s.type === statusType);
      if (existingIndex !== -1) {
        playerStatusEffects.splice(existingIndex, 1);
      }
    }

    const durationValue = options.duration !== undefined ? options.duration : (meta.defaultDuration ?? 1);
    const statusEffect: PlayerStatusEffect = {
      id: `${statusType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: statusType,
      source: options.source || null,
      sourceName: options.sourceName || null,
      appliedTurn: options.currentTurn || 0,
      value: options.value || meta.effects?.endPhaseDamage || meta.effects?.summonCostReduction || 0,
      duration: durationValue,
      remainingTurns: durationValue,
    };

    playerStatusEffects.push(statusEffect);

    return { success: true, statusEffect };
  }

  /**
   * プレイヤーの状態異常を処理（エンドフェイズ）
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @returns { damage: number, effects: Array }
   */
  processPlayerEndPhase<T extends { type: StatusEffectTypeValue; value?: number }>(
    playerStatusEffects: T[] | null
  ): { damage: number; effects: T[] } {
    if (!playerStatusEffects || playerStatusEffects.length === 0) {
      return { damage: 0, effects: [] };
    }

    let totalDamage = 0;

    playerStatusEffects.forEach(status => {
      if (status.type === STATUS_EFFECT_TYPES.POISON) {
        totalDamage += status.value || 100; // デフォルト100ダメージ
      }
    });

    // 状態異常配列はそのまま返す（毒は永続なので解除しない）
    return { damage: totalDamage, effects: [...playerStatusEffects] };
  }

  /**
   * プレイヤーの状態異常を解除
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @param statusType - 状態異常タイプ（省略で全解除）
   * @returns 解除された状態異常
   */
  removePlayerStatus(
    playerStatusEffects: PlayerStatusEffect[] | null,
    statusType: StatusEffectTypeValue | null = null
  ): PlayerStatusEffect[] {
    if (!playerStatusEffects) {
      return [];
    }

    let removed: PlayerStatusEffect[] = [];

    if (statusType) {
      const index = playerStatusEffects.findIndex(s => s.type === statusType);
      if (index !== -1) {
        removed = playerStatusEffects.splice(index, 1);
      }
    } else {
      removed = [...playerStatusEffects];
      playerStatusEffects.length = 0;
    }

    return removed;
  }

  /**
   * プレイヤーが特定の状態異常を持っているか
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @param statusType - 状態異常タイプ
   * @returns boolean
   */
  hasPlayerStatus(
    playerStatusEffects: PlayerStatusEffect[] | null,
    statusType: StatusEffectTypeValue
  ): boolean {
    return playerStatusEffects?.some(s => s.type === statusType) || false;
  }

  /**
   * 属性別召喚コスト軽減量を取得
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @param attribute - カードの属性
   * @returns 軽減量（0以上）
   */
  getSummonCostReduction(
    playerStatusEffects: Array<{ type: StatusEffectTypeValue; value?: number }> | null,
    attribute: string
  ): number {
    if (!playerStatusEffects || playerStatusEffects.length === 0) {
      return 0;
    }

    let totalReduction = 0;

    playerStatusEffects.forEach(status => {
      const meta = STATUS_EFFECT_METADATA[status.type];
      if (meta?.effects?.summonCostReduction && meta?.effects?.targetAttribute === attribute) {
        totalReduction += status.value || meta.effects.summonCostReduction;
      }
    });

    return totalReduction;
  }

  /**
   * SP増加減少量を取得
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @returns SP増加減少量（0以上）
   */
  getSPGainReduction(
    playerStatusEffects: Array<{ type: StatusEffectTypeValue; value?: number }> | null
  ): number {
    if (!playerStatusEffects || playerStatusEffects.length === 0) {
      return 0;
    }

    let totalReduction = 0;

    playerStatusEffects.forEach(status => {
      if (status.type === STATUS_EFFECT_TYPES.SP_GAIN_REDUCTION) {
        const meta = STATUS_EFFECT_METADATA[status.type];
        totalReduction += status.value || meta?.effects?.spGainReduction || 1;
      }
    });

    return totalReduction;
  }

  /**
   * SP増加減少の状態異常を解除
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @returns 解除後の配列
   */
  removeSPGainReduction<T extends { type: StatusEffectTypeValue }>(
    playerStatusEffects: T[] | null
  ): T[] {
    if (!playerStatusEffects || playerStatusEffects.length === 0) {
      return [];
    }

    return playerStatusEffects.filter(
      status => status.type !== STATUS_EFFECT_TYPES.SP_GAIN_REDUCTION
    );
  }

  /**
   * プレイヤーがダイレクトアタックできるか判定
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @returns ダイレクトアタック可能ならtrue
   */
  canDirectAttack(
    playerStatusEffects: Array<{ type: StatusEffectTypeValue }> | null
  ): boolean {
    if (!playerStatusEffects || playerStatusEffects.length === 0) {
      return true;
    }

    return !playerStatusEffects.some(
      status => status.type === STATUS_EFFECT_TYPES.NO_DIRECT_ATTACK
    );
  }

  /**
   * ダイレクトアタック禁止の状態異常を解除
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @returns 解除後の配列
   */
  removeNoDirectAttack<T extends { type: StatusEffectTypeValue }>(
    playerStatusEffects: T[] | null
  ): T[] {
    if (!playerStatusEffects || playerStatusEffects.length === 0) {
      return [];
    }

    return playerStatusEffects.filter(
      status => status.type !== STATUS_EFFECT_TYPES.NO_DIRECT_ATTACK
    );
  }

  /**
   * プレイヤー状態異常のターン開始処理
   * @param playerStatusEffects - プレイヤーの状態異常配列
   * @returns { updatedEffects: Array, expiredEffects: Array }
   */
  processPlayerTurnStart(playerStatusEffects: PlayerStatusEffect[] | null): ProcessPlayerTurnStartResult {
    if (!playerStatusEffects || playerStatusEffects.length === 0) {
      return { updatedEffects: [], expiredEffects: [] };
    }

    const expiredEffects: PlayerStatusEffect[] = [];
    const updatedEffects = playerStatusEffects.filter(status => {
      // 永続効果（duration: -1）は解除しない
      if (status.duration === -1 || status.remainingTurns === -1) {
        return true;
      }

      // remainingTurns を減少
      if (status.remainingTurns !== undefined && status.remainingTurns > 0) {
        status.remainingTurns--;
      }

      // 残りターンが0以下なら解除
      if (status.remainingTurns !== undefined && status.remainingTurns <= 0) {
        expiredEffects.push(status);
        return false;
      }

      return true;
    });

    return { updatedEffects, expiredEffects };
  }
}

// シングルトンインスタンス
export const statusEffectEngine = new StatusEffectEngine();
